/**
 * Platform Admin API Client
 */

import axios, { AxiosInstance } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface PlatformAdmin {
  id: string;
  email: string;
  displayName?: string;
  role: 'superadmin' | 'admin' | 'support';
  status: 'active' | 'suspended';
  lastLoginAt?: string;
  createdAt: string;
}

export interface Organization {
  id: string;
  slug: string;
  name: string;
  plan: 'free' | 'pro' | 'enterprise';
  subscriptionStatus: string;
  maxRooms: number;
  maxUsers: number;
  maxMonthlyMinutes: number;
  minutesUsed: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;  // Subscription expiration date
  createdAt: string;
  updatedAt: string;
}

export interface TimePackage {
  id: string;
  organizationId: string;
  packageType: string;
  hoursPurchased: number;
  hoursRemaining: number;
  pricePerHour: number;
  totalPrice: number;
  purchasedAt: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'depleted';
  paymentMethod: string;
  depositorName?: string;
  transferDate?: string;
  bankReference?: string;
  notes?: string;
  processedBy?: string;
  createdAt: string;
}

export interface TimePackagePricing {
  packageType: string;
  hours: number;
  pricePerHour: number;
  totalPrice: number;
  discountPercent: number;
  validityMonths: number;
  isEnterprise: boolean;
}

export interface OrganizationTimeBalance {
  totalHoursRemaining: number;
  planHoursRemaining: number;
  packageHoursRemaining: number;
  activePackages: TimePackage[];
  nextExpiration?: string;
}

export interface OrganizationUser {
  id: string;
  email: string;
  displayName?: string;
  role: 'owner' | 'admin' | 'operator' | 'member';
  status: 'active' | 'invited' | 'suspended';
  lastLoginAt?: string;
  createdAt: string;
}

export interface PlatformStats {
  organizations: {
    total: number;
    byPlan: Record<string, number>;
  };
  users: {
    total: number;
    active: number;
  };
  rooms: {
    total: number;
    active: number;
  };
  usage: {
    totalMinutes: number;
    thisMonth: number;
  };
}

export interface UsageLog {
  id: string;
  organizationId: string;
  organizationName: string | null;
  organizationSlug: string | null;
  timePackageId: string | null;
  roomId: string | null;
  roomName: string | null;
  minutesUsed: number;
  hoursBefore: number;
  hoursAfter: number;
  sessionId: string | null;
  startedAt: string;
  endedAt: string;
  createdAt: string;
}

export interface AuditLog {
  id: string;
  organizationId: string;
  organizationName: string | null;
  organizationSlug: string | null;
  userId: string | null;
  userEmail: string | null;
  userDisplayName: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface PlatformAuditLog {
  id: string;
  platformAdminId: string | null;
  adminEmail: string | null;
  adminDisplayName: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface LogsFilter {
  orgId?: string;
  adminId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface BankInfo {
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  billingEmail: string;
}

class PlatformApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth header to all requests
    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });
  }

  setToken(token: string | null) {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  // ============================================
  // AUTHENTICATION
  // ============================================

  async login(email: string, password: string): Promise<{ admin: PlatformAdmin; tokens: { accessToken: string; refreshToken: string } }> {
    const response = await this.client.post('/api/v1/platform/auth/login', { email, password });
    return response.data.data;
  }

  async refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> {
    const response = await this.client.post('/api/v1/platform/auth/refresh', { refreshToken });
    return response.data.data;
  }

  async getCurrentAdmin(): Promise<PlatformAdmin> {
    const response = await this.client.get('/api/v1/platform/auth/me');
    return response.data.data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/v1/platform/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(email: string, token: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/v1/platform/auth/reset-password', { email, token, newPassword });
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.put('/api/v1/platform/auth/change-password', { currentPassword, newPassword });
    return response.data;
  }

  // ============================================
  // PLATFORM STATS
  // ============================================

  async getStats(): Promise<PlatformStats> {
    const response = await this.client.get('/api/v1/platform/stats');
    return response.data.data;
  }

  // ============================================
  // ORGANIZATIONS
  // ============================================

  async listOrganizations(page = 1, pageSize = 20): Promise<{ organizations: Organization[]; total: number }> {
    const response = await this.client.get('/api/v1/platform/organizations', {
      params: { page, pageSize },
    });
    return response.data.data;
  }

  async updateOrganizationPlan(
    orgId: string,
    plan: string,
    limits?: Record<string, number>,
    subscriptionEndsAt?: string | null
  ): Promise<Organization> {
    const response = await this.client.put(`/api/v1/platform/organizations/${orgId}/plan`, {
      plan,
      limits,
      subscriptionEndsAt,
    });
    return response.data.data.organization;
  }

  async deleteOrganization(orgId: string): Promise<void> {
    await this.client.delete(`/api/v1/platform/organizations/${orgId}`);
  }

  async createOrganization(data: {
    name: string;
    slug: string;
    plan?: 'free' | 'pro' | 'enterprise';
    ownerEmail: string;
    ownerPassword: string;
    ownerDisplayName?: string;
  }): Promise<{ organization: Organization; ownerId: string }> {
    const response = await this.client.post('/api/v1/platform/organizations', data);
    return response.data.data;
  }

  // ============================================
  // TIME PACKAGES
  // ============================================

  async getOrganizationBalance(orgId: string): Promise<OrganizationTimeBalance> {
    const response = await this.client.get(`/api/v1/platform/organizations/${orgId}/balance`);
    return response.data.data;
  }

  async listOrganizationPackages(orgId: string, includeInactive = false): Promise<TimePackage[]> {
    const response = await this.client.get(`/api/v1/platform/organizations/${orgId}/packages`, {
      params: { includeInactive },
    });
    return response.data.data.packages;
  }

  async createTimePackage(
    orgId: string,
    data: {
      packageType: string;
      paymentMethod?: string;
      depositorName?: string;
      transferDate?: string;
      bankReference?: string;
      notes?: string;
    }
  ): Promise<TimePackage> {
    const response = await this.client.post(`/api/v1/platform/organizations/${orgId}/packages`, data);
    return response.data.data.package;
  }

  async getOrganizationUsageLogs(orgId: string, page = 1, pageSize = 50): Promise<{ logs: any[]; total: number }> {
    const response = await this.client.get(`/api/v1/platform/organizations/${orgId}/usage-logs`, {
      params: { page, pageSize },
    });
    return response.data.data;
  }

  async resetOrganizationUsage(orgId: string, notes?: string): Promise<void> {
    await this.client.post(`/api/v1/platform/organizations/${orgId}/reset-usage`, { notes });
  }

  async deleteTimePackage(packageId: string, reason?: string): Promise<void> {
    await this.client.delete(`/api/v1/platform/packages/${packageId}`, {
      data: { reason },
    });
  }

  async adjustTimePackageHours(
    packageId: string,
    hoursAdjustment: number,
    reason: string
  ): Promise<TimePackage> {
    const response = await this.client.post(`/api/v1/platform/packages/${packageId}/adjust`, {
      hoursAdjustment,
      reason,
    });
    return response.data.data.package;
  }

  // ============================================
  // PRICING & SETTINGS
  // ============================================

  async getPricing(): Promise<{ packages: TimePackagePricing[]; bankInfo: BankInfo }> {
    const response = await this.client.get('/api/v1/platform/pricing');
    return response.data.data;
  }

  async getSettings(): Promise<{ settings: Record<string, string>; bankInfo: BankInfo }> {
    const response = await this.client.get('/api/v1/platform/settings');
    return response.data.data;
  }

  async updateSettings(settings: Record<string, string>): Promise<void> {
    await this.client.put('/api/v1/platform/settings', settings);
  }

  // ============================================
  // PLATFORM ADMINS
  // ============================================

  async listAdmins(): Promise<PlatformAdmin[]> {
    const response = await this.client.get('/api/v1/platform/admins');
    return response.data.data.admins;
  }

  async createAdmin(data: { email: string; password: string; displayName?: string; role?: string }): Promise<PlatformAdmin> {
    const response = await this.client.post('/api/v1/platform/admins', data);
    return response.data.data.admin;
  }

  // ============================================
  // ORGANIZATION USERS
  // ============================================

  async listOrganizationUsers(orgId: string): Promise<OrganizationUser[]> {
    const response = await this.client.get(`/api/v1/platform/organizations/${orgId}/users`);
    return response.data.data.users;
  }

  async updateOrganizationUser(
    orgId: string,
    userId: string,
    data: { email?: string; password?: string; displayName?: string }
  ): Promise<OrganizationUser> {
    const response = await this.client.put(`/api/v1/platform/organizations/${orgId}/users/${userId}`, data);
    return response.data.data.user;
  }

  // ============================================
  // LOGS
  // ============================================

  async getUsageLogs(filter: LogsFilter = {}): Promise<{ logs: UsageLog[]; pagination: Pagination }> {
    const params = new URLSearchParams();
    if (filter.orgId) params.set('orgId', filter.orgId);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    if (filter.page) params.set('page', filter.page.toString());
    if (filter.limit) params.set('limit', filter.limit.toString());

    const response = await this.client.get(`/api/v1/platform/logs/usage?${params.toString()}`);
    return response.data.data;
  }

  async getAuditLogs(filter: LogsFilter = {}): Promise<{ logs: AuditLog[]; pagination: Pagination }> {
    const params = new URLSearchParams();
    if (filter.orgId) params.set('orgId', filter.orgId);
    if (filter.action) params.set('action', filter.action);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    if (filter.page) params.set('page', filter.page.toString());
    if (filter.limit) params.set('limit', filter.limit.toString());

    const response = await this.client.get(`/api/v1/platform/logs/audit?${params.toString()}`);
    return response.data.data;
  }

  async getPlatformAuditLogs(filter: LogsFilter = {}): Promise<{ logs: PlatformAuditLog[]; pagination: Pagination }> {
    const params = new URLSearchParams();
    if (filter.adminId) params.set('adminId', filter.adminId);
    if (filter.action) params.set('action', filter.action);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    if (filter.page) params.set('page', filter.page.toString());
    if (filter.limit) params.set('limit', filter.limit.toString());

    const response = await this.client.get(`/api/v1/platform/logs/platform-audit?${params.toString()}`);
    return response.data.data;
  }
}

export const platformClient = new PlatformApiClient();
